import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { CONFIG, formatPrice } from "../config";
import { CHART_DATA, CHART_PERIODS } from "../mock";
import { Reveal } from "../hooks/useReveal";
import { useLivePrice, directionColor } from "../hooks/useLivePrice";

const WIDTH = 640;
const HEIGHT = 300;
const PAD = { top: 20, right: 10, bottom: 20, left: 10 };

function Chart({ data, animKey }) {
  const { path, length, dot } = useMemo(() => {
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const innerW = WIDTH - PAD.left - PAD.right;
    const innerH = HEIGHT - PAD.top - PAD.bottom;
    const pts = data.map((v, i) => {
      const x = PAD.left + (i / (data.length - 1)) * innerW;
      const y = PAD.top + innerH - ((v - min) / range) * innerH;
      return [x, y];
    });
    const d = pts
      .map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`))
      .join(" ");
    let len = 0;
    for (let i = 1; i < pts.length; i++) {
      len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    }
    const last = pts[pts.length - 1];
    return {
      path: d,
      length: Math.ceil(len),
      dot: { x: (last[0] / WIDTH) * 100, y: (last[1] / HEIGHT) * 100 },
    };
  }, [data]);

  return (
    <div className="relative w-full h-[220px] sm:h-[280px]">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-full"
        preserveAspectRatio="none"
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={PAD.top + f * (HEIGHT - PAD.top - PAD.bottom)}
            y2={PAD.top + f * (HEIGHT - PAD.top - PAD.bottom)}
            stroke="rgba(240,241,238,0.05)"
            strokeWidth="1"
          />
        ))}
        <path
          key={animKey}
          d={path}
          fill="none"
          stroke="#DADCD9"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: length,
            strokeDashoffset: 0,
            animation: `draw-line 1.6s cubic-bezier(0.22,1,0.36,1) forwards`,
            ["--dash"]: length,
          }}
        />
      </svg>
      {/* Live endpoint marker */}
      <div
        className="absolute w-[8px] h-[8px] -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
        style={{
          left: `${dot.x}%`,
          top: `${dot.y}%`,
          background: "#F0F1EE",
          boxShadow: "0 0 10px rgba(240,241,238,0.7)",
          transition: "left 0.9s linear, top 0.9s ease-out",
        }}
      >
        <span
          className="pulse-dot absolute inset-0 rounded-full"
          style={{ background: "rgba(240,241,238,0.35)", transform: "scale(2.2)" }}
        />
      </div>
    </div>
  );
}

export default function Market() {
  const { price, direction } = useLivePrice();
  const [period, setPeriod] = useState("7D");
  const [series, setSeries] = useState(() => [...CHART_DATA["7D"]]);

  // Reseed the rolling series whenever the period changes.
  useEffect(() => {
    setSeries([...CHART_DATA[period]]);
  }, [period]);

  // Append each live price tick to the rolling window.
  useEffect(() => {
    setSeries((prev) => {
      if (!prev.length) return [price];
      return [...prev.slice(1), price];
    });
  }, [price]);

  const priceColor = directionColor(direction);

  return (
    <section id="market" className="w-full py-[80px] lg:py-[140px] bg-[#0D0E0E] border-y hairline">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-[64px]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — token summary */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-baseline gap-3">
                <span className="text-[24px] font-semibold tracking-tight text-[#F1F2EF]">
                  {CONFIG.token}
                </span>
                <span className="text-[12px] text-[#A0A5A2]">
                  1 {CONFIG.token} = 10 mg Pt
                </span>
              </div>
              <div className="mt-1 text-[12px] tracking-[0.14em] uppercase text-[#6D726F]">
                Platinum · Real World Asset
              </div>

              <div
                className="mt-8 text-[52px] sm:text-[64px] font-medium tracking-[-0.03em] leading-none tabular-nums"
                style={{ color: priceColor, transition: "color 0.5s ease" }}
              >
                {formatPrice(price)}
              </div>

              <div className="mt-8 grid grid-cols-2 gap-px max-w-[360px] border-t border-l hairline">
                <div className="border-b border-r hairline px-5 py-4">
                  <div className="micro-label">Chain</div>
                  <div className="mt-1.5 text-[15px] text-[#F1F2EF]">{CONFIG.network}</div>
                </div>
                <div className="border-b border-r hairline px-5 py-4">
                  <div className="micro-label">Per Token</div>
                  <div className="mt-1.5 text-[15px] text-[#F1F2EF]">10 mg Pt</div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href={CONFIG.buyUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#F0F1EE] px-6 py-3 text-[14px] font-medium text-[#080909] hover:bg-white transition-colors duration-200"
                >
                  Buy {CONFIG.token}
                  <ArrowRight size={16} strokeWidth={2} />
                </a>
                <a
                  href={CONFIG.whitepaperUrl || "#"}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-[#F0F1EE]/15 px-6 py-3 text-[14px] font-medium text-[#F1F2EF] hover:border-[#F0F1EE]/30 transition-colors duration-200"
                >
                  Whitepaper
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right — chart */}
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.08}>
              <div className="border hairline rounded-[6px] bg-[#080909] p-5 sm:p-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="micro-label">XPTY Market</span>
                    <span className="flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase text-[#8FB79A]">
                      <span className="pulse-dot inline-block w-[5px] h-[5px] rounded-full bg-[#8FB79A]" />
                      Live
                    </span>
                  </div>
                  <div className="flex items-center gap-1 self-start sm:self-auto">
                    {CHART_PERIODS.map((p) => (
                      <button
                        key={p}
                        onClick={() => setPeriod(p)}
                        className={`px-3.5 py-2.5 text-[11px] tracking-[0.08em] rounded-full transition-colors duration-200 ${
                          period === p
                            ? "bg-[#131515] text-[#F1F2EF]"
                            : "text-[#6D726F] hover:text-[#A0A5A2]"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-6">
                  <Chart data={series} animKey={period} />
                </div>
                <div className="mt-4 flex items-center justify-between text-[11px] text-[#6D726F]">
                  <span>Simulated price movement · not live market data</span>
                  <span
                    className="tabular-nums"
                    style={{ color: priceColor, transition: "color 0.5s ease" }}
                  >
                    {formatPrice(price)}
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
